import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDrugSearchTabs } from "../../components/enhancedSearch/AppSearchPage/AppDrugSearchTabs";
import AppSearchBox from "../../components/enhancedSearch/AppSearchBox/AppSearchBox";
import style from "./appModePage.module.scss";
import useDebounce from "../../hooks/useDebounce";
import {
  clearRecentDrugSearchesThunk,
  clearDrugSearchResults,
  clearDrugSearchAutoComplete,
  drugSearchAutocompleteThunk,
  drugSearchSuggestionsThunk,
  drugSearchThunk,
  loadSearchMetaData,
  selectDrugSearchAutoCompleteQueryPlusText,
  selectRecentSearches,
  selectDrugSearchResults,
  selectDrugSearchIsLoading,
  selectDisplayDrugSearchResults,
} from "../enhancedSearch";
import { DrugSearchRecent } from "../../components/enhancedSearch/AppDrugPage/DrugSearchRecent";
import AppDrugSearchResults from "../../components/enhancedSearch/AppDrugPage/DrugSearchResults";
import Loader from "../../components/Loader/Loader";

const DrugLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputFieldRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchRequestThunk, setSearchRequestThunk] = useState(null);

  const dispatch = useDispatch();
  const { debounceFn } = useDebounce();
  const autoCompleteText = useSelector(
    selectDrugSearchAutoCompleteQueryPlusText,
  );
  const recentSearches = useSelector(selectRecentSearches);
  const drugSearchResults = useSelector(selectDrugSearchResults);
  const drugSearchLoading = useSelector(selectDrugSearchIsLoading);
  const displaySearchResults = useSelector(selectDisplayDrugSearchResults);
  const [searchParams] = useSearchParams();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const typeAheadAutoComplete = useCallback(
    debounceFn((searchTerm) => {
      dispatch(drugSearchAutocompleteThunk({ searchText: searchTerm }));
    }, 600),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const typeAheadSuggestions = useCallback(
    debounceFn((searchTerm) => {
      dispatch(drugSearchSuggestionsThunk({ searchText: searchTerm }));
    }, 600),
    [],
  );

  const keyupSearchHandler = async (e) => {
    const searchInputValue = e.target.value.toLowerCase().trim();

    if (searchInputValue.length !== 0) {
      keypressSearchHandler(e);
      typeAheadSuggestions(searchInputValue);
      dispatch(clearDrugSearchAutoComplete());
      typeAheadAutoComplete(searchInputValue);
    }
  };

  const keypressSearchHandler = (event) => {
    if (event.keyCode === 13 && searchValue.length >= 3) {
      if (searchRequestThunk) {
        searchRequestThunk.abort();
      }
      const searchRequest = dispatch(
        drugSearchThunk({ searchText: searchValue }),
      );
      setSearchRequestThunk(searchRequest);
      inputFieldRef.current.blur();
      navigate(`/app/drugLanding?search=${searchValue}`, {
        replace: true,
      });
    }
  };

  const keydownSearchHandler = (e) => {
    if (
      (e.keyCode === 9 || e.key === "Tab") &&
      searchValue.length >= 3 &&
      autoCompleteText !== searchValue &&
      autoCompleteText !== ""
    ) {
      setSearchValue(autoCompleteText);
      e.preventDefault();
    }
  };

  const goBack = () => {
    if (searchRequestThunk) {
      searchRequestThunk.abort();
    }
    dispatch(clearDrugSearchResults());
    navigate("/app/drugLanding");
  };

  const clearSearchHistory = () => {
    dispatch(clearRecentDrugSearchesThunk());
  };

  useEffect(() => {
    dispatch(clearDrugSearchResults());
    dispatch(clearDrugSearchAutoComplete());
    dispatch(loadSearchMetaData());
  }, [dispatch]);

  useEffect(() => {
    const searchQueryParam = searchParams?.get("search")?.trim();
    if (searchQueryParam && searchQueryParam !== searchValue) {
      if (searchRequestThunk) {
        searchRequestThunk.abort();
      }
      const searchRequest = dispatch(
        drugSearchThunk({ searchText: searchQueryParam }),
      );
      setSearchRequestThunk(searchRequest);
      setSearchValue(searchQueryParam);
    }
  }, [location]);

  return (
    <div className={style.searchContainer}>
      <AppSearchBox
        searchValue={searchValue}
        keydownSearchHandler={keydownSearchHandler}
        keyupSearchHandler={keyupSearchHandler}
        setSearchValue={setSearchValue}
        inputFieldRef={inputFieldRef}
        clearSearch={goBack}
        goBack={goBack}
        suggestedText={autoCompleteText}
        push={navigate}
      ></AppSearchBox>
      {drugSearchLoading ? (
        <Loader isAppMode={true} />
      ) : (
        <>
          {displaySearchResults ? (
            <AppDrugSearchResults
              loadingStatus={drugSearchLoading}
              drugResults={drugSearchResults}
            />
          ) : searchValue.trim() ? (
            <AppDrugSearchTabs push={navigate} searchValue={searchValue} />
          ) : (
            <DrugSearchRecent
              push={navigate}
              recentSearches={recentSearches}
              onClearSearch={clearSearchHistory}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DrugLandingPage;
